import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useAsset } from '../hooks/useAsset';

interface EssenceButtonBackgroundProps {
    isActive: boolean;
    canActivate: boolean;
    triggerAnimation: number; // Increment to trigger
}

const EssenceButtonBackground: React.FC<EssenceButtonBackgroundProps> = ({ isActive, canActivate, triggerAnimation }) => {
    const rotationControls = useAnimation();
    const opacityControls = useAnimation();
    const essenceBtn = useAsset('ui.essence.btn');
    const essenceBtn2 = useAsset('ui.essence.btn2');

    useEffect(() => {
        if (triggerAnimation > 0) {
            rotationControls.start({
                rotate: [0, 360],
                transition: {
                    duration: 1.5,
                    times: [0, 1],
                    ease: "easeInOut",
                    type: "spring",
                    stiffness: 100,
                    damping: 10
                }
            });
        }
    }, [triggerAnimation, rotationControls]);

    useEffect(() => {
        if (isActive) {
            // Transition to active state then pulse
            opacityControls.start({
                opacity: 1,
                transition: { duration: 1.5, ease: "easeInOut" }
            }).then(() => {
                opacityControls.start({
                    opacity: [1, 0.6, 1], // Pulsate between 100% and 60%
                    transition: {
                        repeat: Infinity,
                        duration: 2,
                        ease: "easeInOut"
                    }
                });
            });
        } else {
            // Transition back to inactive
            opacityControls.start({
                opacity: 0,
                transition: { duration: 1.5, ease: "easeInOut" }
            });
        }
    }, [isActive, opacityControls]);

    return (
        <motion.div
            className={`absolute inset-0 rounded-full w-full h-full pointer-events-none`}
            animate={rotationControls}
        >
            {/* Base state - essence_btn.png - Always visible as background */}
            <div
                className="absolute inset-0 rounded-full w-full h-full bg-cover bg-center"
                style={{ backgroundImage: `url(${essenceBtn})` }}
            />

            {/* Active state - essence_btn2.png - Overlays base, transitions and pulses */}
            <motion.div
                className="absolute inset-0 rounded-full w-full h-full bg-cover bg-center"
                style={{ backgroundImage: `url(${essenceBtn2})` }}
                initial={{ opacity: 0 }}
                animate={opacityControls}
            />

            {/* Pulse glow overlay */}
            {(!isActive && canActivate) && (
                <div className="absolute inset-0 rounded-full animate-pulse-glow" style={{ animation: 'pulse-glow 4s infinite' }} />
            )}
        </motion.div>
    );
};

export default EssenceButtonBackground;
